<template>
  <div class="center-map-left">
    <div ref="mapEl" class="pv-map" />
    <div class="map-tooltip-hint">📍 点击站点查看详情</div>
    <div class="map-legend">
      <h5>图例</h5>
      <div class="ml-item">
        <span class="ml-dot" style="background: #00e676" /> 在线
      </div>
      <div class="ml-item">
        <span class="ml-dot" style="background: #ffd600" /> 告警
      </div>
      <div class="ml-item">
        <span class="ml-dot" style="background: #ff5252" /> 离线
      </div>
      <div class="ml-item">
        <span
          class="ml-dot"
          style="background: #ffd700; box-shadow: 0 0 8px rgba(255,215,0,0.6)"
        />
        汇聚点
      </div>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  getSiteNodeList,
  getSiteDetail,
  getConvergencePointDetail,
  getSiteEnvMonitor,
} from "@/network/photovoltaic";

const DEFAULT_CENTER = [35.09, 112.602];
/** 弹窗固定宽度（Leaflet 会写内联 width，需 JS + CSS 同步） */
const POPUP_WIDTH = 232;
/** 与 src/assets/js/mapconfig.js 一致的天地图 key */
const TIANDITU_KEY = "0b7d509b3c03694118feae274bf45af8";
const TIANDITU_SUBDOMAINS = ["0", "1", "2", "3", "4", "5", "6", "7"];
/** 中国范围（含港澳台及南海诸岛），限制平移与瓦片加载 */
const CHINA_BOUNDS = L.latLngBounds(
  L.latLng(3.5, 73.0),
  L.latLng(53.6, 135.1)
);
/** 境外遮罩色，与 .pv-map 背景一致 */
const MAP_MASK_FILL = "#e8eef3";
const CHINA_BOUNDARY_URL = "/static/china-boundary.json";
const WORLD_SHELL = [
  [-90, -180],
  [-90, 180],
  [90, 180],
  [90, -180],
];

function ringSignedArea(ring) {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length;
    s += (ring[j][1] - ring[i][1]) * (ring[j][0] + ring[i][0]);
  }
  return s;
}

function geoRingToLatLng(ring) {
  return ring.map(([lng, lat]) => [lat, lng]);
}

/** 仅取各块国土外环，作为遮罩内洞 */
function chinaExteriorHoles(geometry) {
  const holes = [];
  if (!geometry) return holes;
  if (geometry.type === "Polygon" && geometry.coordinates[0]) {
    holes.push(geoRingToLatLng(geometry.coordinates[0]));
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((poly) => {
      if (poly && poly[0]) holes.push(geoRingToLatLng(poly[0]));
    });
  }
  return holes;
}

/** 外环全球 + 内洞中国，内洞与外环绕向相反才能镂空 */
function buildOutsideChinaMaskRings(geometry) {
  const world = WORLD_SHELL.slice();
  const worldArea = ringSignedArea(world);
  const holes = chinaExteriorHoles(geometry).map((ring) =>
    worldArea * ringSignedArea(ring) > 0 ? ring.slice().reverse() : ring
  );
  return [world, ...holes];
}

function clampBounds(inner, outer) {
  const sw = L.latLng(
    Math.max(inner.getSouth(), outer.getSouth()),
    Math.max(inner.getWest(), outer.getWest())
  );
  const ne = L.latLng(
    Math.min(inner.getNorth(), outer.getNorth()),
    Math.min(inner.getEast(), outer.getEast())
  );
  if (sw.lat >= ne.lat || sw.lng >= ne.lng) return outer;
  return L.latLngBounds(sw, ne);
}

function esc(s) {
  return String(s == null ? "" : s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** site-node-list：status 1-在线 2-离线 3-有告警（仅站点；汇聚点为 null） */
function statusColor(st) {
  if (st == null || st === "") return "#00e676";
  const n = Number(st);
  if (n === 1) return "#00e676";
  if (n === 2) return "#ff5252";
  if (n === 3) return "#ffd600";
  return "#00e676";
}

function statusLabel(st) {
  if (st == null || st === "") return "—";
  const n = Number(st);
  if (n === 1) return "在线";
  if (n === 2) return "离线";
  if (n === 3) return "告警";
  return "在线";
}

function statusEmoji(st) {
  if (st == null || st === "") return "📍";
  const n = Number(st);
  if (n === 2) return "🔴";
  if (n === 3) return "🟡";
  return "🟢";
}

/** site-node-list：type 1-汇聚点 2-站点 */
function isConvergenceNode(node) {
  if (!node) return false;
  const t = node.type;
  if (t === 1 || t === "1") return true;
  if (t === 2 || t === "2") return false;
  const s = String(t || "");
  return /汇聚|汇聚点|convergence|hub|中心|aggregation/i.test(s);
}

/** site-node-list 无坐标时，用 id 在空间上摊开（兜底） */
function fallbackLatLng(id, idx, centerLat, centerLng) {
  const seed = Number(id);
  const i = Number.isFinite(seed) ? seed : idx + 13;
  const angle = (((i % 997) / 997) * 2 * Math.PI + idx * 0.6) % (2 * Math.PI);
  const r = 0.018 + (((i + idx * 11) % 8) / 100);
  return [centerLat + r * Math.cos(angle), centerLng + r * Math.sin(angle)];
}

function formatKwh(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("zh-CN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} kWh`;
}

function formatSelfRate(v, selfRateStr) {
  if (selfRateStr != null && String(selfRateStr).trim() !== "") {
    const s = String(selfRateStr).trim();
    return s.endsWith("%") ? s : `${s}%`;
  }
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const p = n > 0 && n <= 1 ? n * 100 : n;
  return `${(Math.round(p * 10) / 10).toFixed(1)}%`;
}

function isApiOk(res) {
  return res && (res.code === 0 || res.code === 200) && res.data != null;
}

/** 兼容接口文档字段与 snake_case */
function normalizeSiteDetail(raw) {
  if (!raw || typeof raw !== "object") return {};
  return {
    spaceId: raw.spaceId != null ? raw.spaceId : raw.space_id,
    spaceName: raw.spaceName || raw.space_name || "",
    status: raw.status,
    ip: raw.ip || "",
    totalEnergy:
      raw.totalEnergy != null ? raw.totalEnergy : raw.total_energy,
    selfRate: raw.selfRate != null ? raw.selfRate : raw.self_rate,
    selfRateStr: raw.selfRateStr || raw.self_rate_str || "",
  };
}

/** site-node-list 的 id 与 site-detail 的 spaceId 对齐 */
function buildEnvLookups(monitors) {
  const bySpaceId = Object.create(null);
  const byName = Object.create(null);
  (monitors || []).forEach((m) => {
    if (!m) return;
    if (m.spaceId != null) bySpaceId[String(m.spaceId)] = m;
    const name = (m.spaceName || m.name || "").trim();
    if (name) byName[name] = m;
  });
  return { bySpaceId, byName };
}

function enrichSiteNode(node, envLookups) {
  const name = (node.name || "").trim();
  const env =
    (node.spaceId != null && envLookups.bySpaceId[String(node.spaceId)]) ||
    envLookups.bySpaceId[String(node.id)] ||
    (name ? envLookups.byName[name] : null);
  const spaceId =
    node.spaceId != null
      ? node.spaceId
      : env && env.spaceId != null
        ? env.spaceId
        : node.id;
  return { ...node, spaceId, _env: env };
}

export default {
  name: "PvLeafletMap",
  data() {
    return {
      map: null,
      markersLayer: null,
      chinaMaskLayer: null,
      refreshTimer: null,
    };
  },
  mounted() {
    this.$nextTick(() => this.initMap());
    this.refreshTimer = setInterval(() => this.reloadMarkers(), 120000);
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.chinaMaskLayer && this.map) {
      this.map.removeLayer(this.chinaMaskLayer);
      this.chinaMaskLayer = null;
    }
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  },
  methods: {
    initMap() {
      const el = this.$refs.mapEl;
      if (!el || this.map) return;
      this.map = L.map(el, {
        center: DEFAULT_CENTER,
        zoom: 12,
        zoomControl: true,
        attributionControl: false,
        minZoom: 4,
        maxBounds: CHINA_BOUNDS,
        maxBoundsViscosity: 1,
        worldCopyJump: false,
      });
      const tdtOpts = {
        subdomains: TIANDITU_SUBDOMAINS,
        minZoom: 4,
        maxZoom: 18,
        bounds: CHINA_BOUNDS,
        noWrap: true,
      };
      L.tileLayer(
        `https://t{s}.tianditu.gov.cn/DataServer?T=vec_w&x={x}&y={y}&l={z}&tk=${TIANDITU_KEY}`,
        { ...tdtOpts, opacity: 0.88 }
      ).addTo(this.map);
      L.tileLayer(
        `https://t{s}.tianditu.gov.cn/DataServer?T=cva_w&x={x}&y={y}&l={z}&tk=${TIANDITU_KEY}`,
        tdtOpts
      ).addTo(this.map);
      this.markersLayer = L.layerGroup().addTo(this.map);
      this.loadOutsideChinaMask();
      this.reloadMarkers();
    },
    /** 国界外遮罩（浅色），境内正常显示地图 */
    loadOutsideChinaMask() {
      if (!this.map || this.chinaMaskLayer) return;
      const pane = this.map.createPane("chinaMaskPane");
      if (pane) pane.style.zIndex = "450";
      fetch(CHINA_BOUNDARY_URL)
        .then((r) => r.json())
        .then((geo) => {
          if (!this.map || this.chinaMaskLayer) return;
          const feature =
            geo && geo.features && geo.features[0] ? geo.features[0] : null;
          const latlngs = buildOutsideChinaMaskRings(
            feature && feature.geometry
          );
          if (latlngs.length < 2) return;
          this.chinaMaskLayer = L.polygon(latlngs, {
            pane: "chinaMaskPane",
            fillColor: MAP_MASK_FILL,
            fillOpacity: 1,
            stroke: false,
            interactive: false,
          }).addTo(this.map);
        })
        .catch(() => {});
    },
    buildCoordLookup(monitors) {
      const lookup = Object.create(null);
      (monitors || []).forEach((m) => {
        if (!m || m.latitude == null || m.longitude == null) return;
        const lat = Number(m.latitude);
        const lng = Number(m.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        lookup[String(m.spaceId)] = [lat, lng];
      });
      return lookup;
    },
    resolveLngLat(node, idx, coordBySpaceId) {
      const spaceId = node.spaceId != null ? node.spaceId : node.id;
      let lat =
        node.latitude != null
          ? Number(node.latitude)
          : node.lat != null
            ? Number(node.lat)
            : null;
      let lng =
        node.longitude != null
          ? Number(node.longitude)
          : node.lng != null
            ? Number(node.lng)
            : null;
      if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
      const fromEnv =
        coordBySpaceId[String(spaceId)] || coordBySpaceId[String(node.id)];
      if (fromEnv) return fromEnv;
      if (node._env) {
        const elat = Number(node._env.latitude);
        const elng = Number(node._env.longitude);
        if (Number.isFinite(elat) && Number.isFinite(elng)) return [elat, elng];
      }
      return fallbackLatLng(spaceId, idx, DEFAULT_CENTER[0], DEFAULT_CENTER[1]);
    },
    flagIcon(color) {
      const flagSvg =
        `<svg width="28" height="34" viewBox="0 0 28 34" xmlns="http://www.w3.org/2000/svg">` +
        `<line x1="8" y1="4" x2="8" y2="32" stroke="${color}" stroke-width="3" stroke-linecap="round"/>` +
        `<polygon points="8,4 24,10 8,16" fill="${color}" stroke="rgba(255,255,255,0.6)" stroke-width="0.5"/>` +
        `<circle cx="8" cy="32" r="2" fill="${color}"/>` +
        `</svg>`;
      return L.divIcon({
        className: "",
        html: `<div style="filter:drop-shadow(0 2px 6px ${color}80);cursor:pointer;text-align:center">${flagSvg}</div>`,
        iconSize: [28, 34],
        iconAnchor: [14, 34],
        popupAnchor: [0, -38],
      });
    },
    convergenceIcon() {
      const html =
        `<div style="width:22px;height:22px;background:#ffd700;border:3px solid #fff;border-radius:3px;` +
        `transform:rotate(45deg);box-shadow:0 0 16px rgba(255,215,0,0.8);display:flex;` +
        `align-items:center;justify-content:center">` +
        `<span style="transform:rotate(-45deg);font-size:10px;color:#000;font-weight:bold">★</span>` +
        `</div>`;
      return L.divIcon({
        className: "",
        html,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
        popupAnchor: [0, -28],
      });
    },
    /** 仅修正宽度；勿在已 setContent 后再无意义 update，否则会还原为 bindPopup 时的「加载中」 */
    fixPopupWidth(marker) {
      const popup = marker && marker.getPopup && marker.getPopup();
      if (!popup) return;
      const fix = () => {
        const el = popup.getElement && popup.getElement();
        if (!el) return;
        const w = `${POPUP_WIDTH}px`;
        const wrapper = el.querySelector(".leaflet-popup-content-wrapper");
        const content = el.querySelector(".leaflet-popup-content");
        if (wrapper) {
          wrapper.style.minWidth = w;
          wrapper.style.maxWidth = w;
        }
        if (content) {
          content.style.margin = "0";
          content.style.minWidth = w;
          content.style.width = w;
          content.style.maxWidth = w;
        }
      };
      this.$nextTick(fix);
      setTimeout(fix, 16);
    },
    setMarkerPopupHtml(marker, html) {
      const popup = marker && marker.getPopup && marker.getPopup();
      if (!popup) return;
      popup.setContent(html);
      if (popup.update) popup.update();
      this.fixPopupWidth(marker);
    },
    buildSitePopupHtml(d, node, href) {
      const detail = normalizeSiteDetail(d);
      const status =
        detail.status != null ? detail.status : node.status;
      return (
        `<div class="map-popup pv-map-popup-body">` +
        `<h4 class="map-popup-title">${esc(
          detail.spaceName || node.name || "站点"
        )}</h4>` +
        `<p class="map-popup-row">${statusEmoji(status)} 状态：<b>${esc(
          statusLabel(status)
        )}</b></p>` +
        `<p class="map-popup-row">⚡ 今日总电量：<b class="map-popup-accent">${formatKwh(
          detail.totalEnergy
        )}</b></p>` +
        `<p class="map-popup-row">☀️ 自给率：<b class="map-popup-green">${formatSelfRate(
          detail.selfRate,
          detail.selfRateStr
        )}</b></p>` +
        `<p class="map-popup-meta">IP：${esc(detail.ip || "—")}</p>` +
        `<div class="map-popup-actions">` +
        `<a class="popup-btn" href="${String(href).replace(/"/g, "&quot;")}">🔗 设备列表</a>` +
        `</div></div>`
      );
    },
    deviceListHref(spaceId) {
      try {
        if (this.$router) {
          const r = this.$router.resolve({
            path: "/device-list",
            query: { spaceId: String(spaceId) },
          });
          return r.href;
        }
      } catch (_) {
        /* fall through */
      }
      return `#/device-list?spaceId=${encodeURIComponent(spaceId)}`;
    },
    bindPopupFetch(marker, node, kind) {
      const loading =
        `<div class="map-popup pv-map-popup-body">` +
        `<div class="map-popup-loading">加载中…</div></div>`;
      marker.bindPopup(loading, {
        maxWidth: POPUP_WIDTH,
        className: "pv-leaflet-popup",
      });
      marker.on("popupopen", () => {
        this.setMarkerPopupHtml(marker, loading);
        const spaceId = node.spaceId != null ? node.spaceId : node.id;
        if (kind === "convergence") {
          getConvergencePointDetail(spaceId)
            .then((res) => {
              if (!isApiOk(res)) {
                this.setMarkerPopupHtml(
                  marker,
                  `<div class="map-popup pv-map-popup-body map-popup--empty">暂无汇聚点详情</div>`
                );
                return;
              }
              const d = res.data;
              let sitesUl = "";
              const sites = Array.isArray(d.sites) ? d.sites : [];
              if (sites.length) {
                sitesUl =
                  `<ul class="map-popup-sites">` +
                  sites
                    .map(
                      (s) =>
                        `<li>${esc(s.name)} <span class="map-popup-site-id">#${
                          s.id != null ? esc(s.id) : ""
                        }</span></li>`
                    )
                    .join("") +
                  `</ul>`;
              }
              this.setMarkerPopupHtml(
                marker,
                `<div class="map-popup pv-map-popup-body">` +
                  `<h4 class="map-popup-title">★ ${esc(
                    d.convergenceName || node.name || "汇聚点"
                  )}</h4>` +
                  `<p class="map-popup-row">下属站点：<b>${
                    d.siteCount != null ? d.siteCount : sites.length
                  }</b> 个</p>` +
                  sitesUl +
                  `</div>`
              );
            })
            .catch(() => {
              this.setMarkerPopupHtml(
                marker,
                `<div class="map-popup pv-map-popup-body map-popup--error">加载失败</div>`
              );
            });
          return;
        }
        getSiteDetail(spaceId)
          .then((res) => {
            if (!isApiOk(res)) {
              const msg = (res && res.message) || "暂无站点详情";
              this.setMarkerPopupHtml(
                marker,
                `<div class="map-popup pv-map-popup-body map-popup--empty">${esc(
                  msg
                )}</div>`
              );
              return;
            }
            const detail = normalizeSiteDetail(res.data);
            const href = this.deviceListHref(
              detail.spaceId != null ? detail.spaceId : spaceId
            );
            this.setMarkerPopupHtml(
              marker,
              this.buildSitePopupHtml(res.data, node, href)
            );
          })
          .catch(() => {
            this.setMarkerPopupHtml(
              marker,
              `<div class="map-popup pv-map-popup-body map-popup--error">加载失败</div>`
            );
          });
      });
    },
    reloadMarkers() {
      if (!this.map || !this.markersLayer) return;
      this.markersLayer.clearLayers();

      Promise.all([getSiteNodeList(), getSiteEnvMonitor()])
        .then(([nodesRes, envRes]) => {
          const nodes =
            nodesRes &&
            nodesRes.code === 0 &&
            Array.isArray(nodesRes.data)
              ? nodesRes.data
              : [];
          const monitors =
            envRes && envRes.code === 0 && Array.isArray(envRes.data)
              ? envRes.data
              : [];
          const coordBySpaceId = this.buildCoordLookup(monitors);
          const envLookups = buildEnvLookups(monitors);

          nodes.forEach((node, idx) => {
            if (!node || node.id == null) return;
            const enriched = enrichSiteNode(node, envLookups);
            const ll = this.resolveLngLat(enriched, idx, coordBySpaceId);
            const convergence = isConvergenceNode(enriched);
            const marker = convergence
              ? L.marker(ll, { icon: this.convergenceIcon() })
              : L.marker(ll, {
                  icon: this.flagIcon(statusColor(enriched.status)),
                });
            marker.bindTooltip(
              esc(enriched.name || `站点${enriched.spaceId}`),
              {
                direction: "top",
                offset: [0, convergence ? -32 : -40],
                className: "flag-tooltip",
              }
            );
            marker.addTo(this.markersLayer);
            this.bindPopupFetch(
              marker,
              enriched,
              convergence ? "convergence" : "site"
            );
          });

          const boundsMarkers = [...this.markersLayer.getLayers()];
          const fitOpts = { padding: [24, 24], maxZoom: 14 };
          if (boundsMarkers.length === 1) {
            const b = boundsMarkers[0];
            const p = b.getLatLng ? b.getLatLng() : null;
            if (p) {
              const clamped = CHINA_BOUNDS.contains(p) ? p : CHINA_BOUNDS.getCenter();
              this.map.setView(clamped, Math.max(this.map.getZoom(), 13));
            }
          } else if (boundsMarkers.length > 1) {
            const grp = L.featureGroup(boundsMarkers);
            this.map.fitBounds(
              clampBounds(grp.getBounds().pad(0.12), CHINA_BOUNDS),
              fitOpts
            );
          }
        })
        .catch(() => {});
    },
    invalidate() {
      if (this.map) {
        setTimeout(() => this.map.invalidateSize(), 50);
      }
    },
  },
};
</script>

